const SELECTORS = {
  root: '[data-mood-slider]',
  trigger: '[data-mood-trigger]',
  modal: '[data-mood-modal]',
  dialog: '[data-mood-dialog]',
  overlay: '[data-mood-overlay]',
  close: '[data-mood-close]',
  panel: '[data-panel-id]',
  heading: '[data-panel-heading]',
};

const ANIMATION_DURATION = 220;
const instances = new WeakMap();

class MoodSlider {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.modal = root.querySelector(SELECTORS.modal);
    this.dialog = root.querySelector(SELECTORS.dialog);
    this.overlay = root.querySelector(SELECTORS.overlay);
    this.closeButton = root.querySelector(SELECTORS.close);
    this.triggers = Array.from(root.querySelectorAll(SELECTORS.trigger));
    this.panels = Array.from(root.querySelectorAll(SELECTORS.panel));
    this.activePanel = null;
    this.lastTrigger = null;
    this.shouldUnlockScroll = false;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);

    if (!this.modal || !this.dialog || !this.triggers.length || !this.panels.length) return;

    this.triggers.forEach((trigger) => trigger.addEventListener('click', this.handleTriggerClick));
    this.overlay?.addEventListener('click', this.handleOverlayClick);
    this.closeButton?.addEventListener('click', () => this.close());
    this.modal.addEventListener('click', this.handleModalClick);

  }

  /**
   * @param {MouseEvent} event
   */
  handleTriggerClick(event) {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const id = target.dataset.target;
    this.open(id, target);
  }

  handleOverlayClick() {
    this.close();
  }

  /**
   * @param {MouseEvent} event
   */
  handleModalClick(event) {
    if (event.target === this.modal) {
      this.close();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * @param {string | undefined} id
   * @param {HTMLElement} [trigger]
   */
  open(id, trigger) {
    if (!id || !this.modal) return;

    const panel = this.panels.find((item) => item.dataset.panelId === id);
    if (!panel) return;

    if (this.activePanel && this.activePanel !== panel) {
      this.activePanel.hidden = true;
      this.activePanel.classList.remove('is-active');
    }

    panel.hidden = false;
    panel.classList.add('is-active');
    this.activePanel = panel;
    this.lastTrigger = trigger || this.lastTrigger;

    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', btn === trigger ? 'true' : 'false'));

    const heading = panel.querySelector(SELECTORS.heading);
    if (heading instanceof HTMLElement && heading.id) {
      this.dialog?.setAttribute('aria-labelledby', heading.id);
    }

    this.modal.removeAttribute('hidden');
    requestAnimationFrame(() => this.modal?.classList.add('is-visible'));

    // Peek feature removed

    if (!document.documentElement.hasAttribute('scroll-lock')) {
      this.shouldUnlockScroll = true;
      document.documentElement.setAttribute('scroll-lock', '');
    } else {
      this.shouldUnlockScroll = false;
    }

    this.closeButton?.focus({ preventScroll: true });
    document.addEventListener('keydown', this.handleKeydown);
  }

  close() {
    if (!this.modal || this.modal.hasAttribute('hidden')) return;

    this.modal.classList.remove('is-visible');
    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    // Peek feature removed

    window.setTimeout(() => {
      this.modal?.setAttribute('hidden', '');

      if (this.activePanel) {
        this.activePanel.hidden = true;
        this.activePanel.classList.remove('is-active');
        this.activePanel = null;
      }
    }, ANIMATION_DURATION);

    if (this.shouldUnlockScroll) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    document.removeEventListener('keydown', this.handleKeydown);

    if (this.lastTrigger instanceof HTMLElement) {
      this.lastTrigger.focus({ preventScroll: true });
    }
  }

  destroy() {
    this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.handleTriggerClick));
    this.overlay?.removeEventListener('click', this.handleOverlayClick);
    this.modal?.removeEventListener('click', this.handleModalClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.close();
  }

}

const initMoodSlider = (root) => {
  if (!(root instanceof HTMLElement) || instances.has(root)) return;
  const instance = new MoodSlider(root);
  instances.set(root, instance);
};

const destroyMoodSlider = (root) => {
  const instance = instances.get(root);
  if (instance?.destroy) {
    instance.destroy();
  }
  instances.delete(root);
};

const initAll = (scope = document) => {
  scope.querySelectorAll(SELECTORS.root).forEach((root) => initMoodSlider(root));
};

initAll();

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  initAll(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  event.target.querySelectorAll(SELECTORS.root).forEach((root) => destroyMoodSlider(root));
});

document.addEventListener('shopify:block:select', (event) => {
  // In editor, avoid forcing the modal open to keep layout visible while configuring blocks.
  if (typeof Shopify !== 'undefined' && Shopify.designMode) return;

  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const blockRoot = target.closest(`${SELECTORS.root}`);
  if (!blockRoot) return;

  const trigger = target.closest(SELECTORS.trigger);
  const panel = target.closest(SELECTORS.panel);
  const instance = instances.get(blockRoot) || new MoodSlider(blockRoot);
  instances.set(blockRoot, instance);

  if (trigger) {
    instance.open(trigger.dataset.target, trigger);
  } else if (panel) {
    instance.open(panel.dataset.panelId);
  }
});
